import { useId, useMemo, type ReactNode } from 'react';
import { cn } from '../../lib/cn';
import { FieldContext } from './fieldContext';

export interface FieldProps {
  label: ReactNode;
  /** Short help shown under the label (linked with aria-describedby). */
  hint?: ReactNode;
  /** Error message (linked with aria-describedby and sets aria-invalid on the control). */
  error?: string | null;
  required?: boolean;
  /** Show "(optional)" after the label. */
  optional?: boolean;
  /** Visually hide the label (still read by screen readers). */
  labelHidden?: boolean;
  /** Explicit control id (otherwise generated). */
  id?: string;
  className?: string;
  children: ReactNode;
}

/**
 * Label + hint + error wrapper for a single control. The control (Input, Select, Textarea, MoneyInput,
 * DateInput) picks up its id and aria attributes automatically from context.
 */
export function Field({ label, hint, error, required, optional, labelHidden, id, className, children }: FieldProps) {
  const autoId = useId();
  const controlId = id ?? `f${autoId.replace(/:/g, '')}`;
  const hintId = hint ? `${controlId}-hint` : undefined;
  const errorId = error ? `${controlId}-error` : undefined;
  const value = useMemo(
    () => ({ id: controlId, hintId, errorId, invalid: Boolean(error), required: Boolean(required) }),
    [controlId, hintId, errorId, error, required],
  );
  return (
    <div className={cn('flex flex-col gap-1', error && 'border-l-4 border-danger pl-3', className)}>
      <label htmlFor={controlId} className={cn('font-bold text-text', labelHidden && 'sr-only')}>
        {label}
        {optional && <span className="font-normal text-muted"> (optional)</span>}
      </label>
      {hint && (
        <p id={hintId} className="text-muted">
          {hint}
        </p>
      )}
      {error && <FieldError id={errorId}>{error}</FieldError>}
      <FieldContext.Provider value={value}>{children}</FieldContext.Provider>
    </div>
  );
}

/** Inline error text with a hidden "Error:" prefix for screen readers. */
export function FieldError({ id, children }: { id?: string; children: ReactNode }) {
  return (
    <p id={id} className="font-bold text-danger">
      <span className="sr-only">Error: </span>
      {children}
    </p>
  );
}

export interface FieldsetProps {
  legend: ReactNode;
  hint?: ReactNode;
  error?: string | null;
  legendHidden?: boolean;
  /** Render the legend as a page-level question (larger). */
  legendSize?: 'md' | 'lg';
  className?: string;
  children: ReactNode;
}

/** Group of related controls (radios, checkboxes, date parts) with legend, hint and error. */
export function Fieldset({ legend, hint, error, legendHidden, legendSize = 'md', className, children }: FieldsetProps) {
  const autoId = useId().replace(/:/g, '');
  const hintId = hint ? `fs${autoId}-hint` : undefined;
  const errorId = error ? `fs${autoId}-error` : undefined;
  return (
    <fieldset
      className={cn('flex min-w-0 flex-col gap-2', error && 'border-l-4 border-danger pl-3', className)}
      aria-describedby={cn(errorId, hintId) || undefined}
      aria-invalid={error ? true : undefined}
    >
      <legend className={cn('mb-1 font-bold text-text', legendSize === 'lg' && 'text-xl', legendHidden && 'sr-only')}>
        {legend}
      </legend>
      {hint && (
        <p id={hintId} className="text-muted">
          {hint}
        </p>
      )}
      {error && <FieldError id={errorId}>{error}</FieldError>}
      {children}
    </fieldset>
  );
}
